// The CLIENT stroke recognizer (K-91f) — pointer events to one of the six
// strokes of the grammar (product-canon file 77 §1), then an hx-post of the
// RECOGNISED stroke to the server, which interprets it through the same pure
// algebra the client has no business owning (internal/stroke over
// internal/object). The server endpoint is POST /challenge/{id}/stroke and the
// payload is a form: `stroke=<kind>` plus one `take=<uuid>` per touched take, in
// touch order (internal/field/write.go PostStroke). NO geometry is ever posted
// or kept past recognition — the touched SET is the only output (file 77 §5).
//
// Framework-free (canon 91 §1): vanilla TS, esbuild only, no SPA framework and
// no runtime dependency. It is the missing half of the grammar — the server
// (internal/stroke) has interpreted the six strokes since K-91e, and the island
// (electric.ts) paints liveness, but nothing turned a person's drag into a
// recognised stroke, so no act could be DRAWN. This module is that.
//
// Canon 83 §1.4 — NO hand, finger, cursor or body is ever drawn. The recognizer
// READS pointer events; it draws the person's INK while the finger is down (raw
// path), and on release SMOOTHS it to the logical shape (the settle moment of
// file 77 §3 / file 83 §1.1: "raw ink in, geometry out"), then the ink fades.
// The pointer itself leaves no cursor mark. The ink is a VERB, not a canvas
// (file 77 §4): its geometry is thrown away once the stroke is recognised; only
// the meaning (the stroke kind + the touched set) is kept.
//
// SSR / test safe: no document access at import time. The classifier
// (`classify`) is a PURE function of a recorded pointer trace + the objects it
// touched, with no DOM and no geometry retained in its output, so it is unit-
// testable with recorded traces (the K-91 evidence). `attachRecognizer` is the
// DOM wiring that feeds live pointer events into it.

// ---- the six strokes (file 77 §1), mirroring internal/stroke.Stroke ---------

export type Stroke = "encircle" | "connect" | "carry" | "scratch" | "pull" | "hold";

/** The object kinds the surface draws, mirroring internal/object.Kind. The
 *  recognizer only ever hit-tests the kinds the field renders as addressable
 *  objects today (takes, challenge rings, keywords); the rest are here so a
 *  touched set types cleanly against the server's grammar. */
export type Kind = "challenge" | "take" | "person" | "reading" | "aim" | "keyword" | "place";

/** Which pane of the terminal an object sits in (file 75). "feed" is the single
 *  (non-split) surface; "left"/"right" are the two split panes (file 75 §3). */
export type Pane = "feed" | "left" | "right";

/** One object the stroke touched, by kind, id and pane. This is the ONLY thing
 *  that leaves recognition about an object — never a coordinate. */
export interface TouchedObject {
  kind: Kind;
  id: string;
  pane: Pane;
}

/** The recognised stroke and the ordered set of objects it touched (file 77 §2:
 *  the touched set is the only output; for connect/carry/pull the first element
 *  is the origin). It carries NO geometry — the whole point of the grammar
 *  (file 77 §5). A `null` result means the movement matched no stroke, so the
 *  surface does nothing and leaves no mark (file 77 §1, §2). */
export interface Recognised {
  stroke: Stroke;
  objects: TouchedObject[];
  /** True when the stroke's two ends sit in the two different panes of a split
   *  (file 75 §3 — the graph made by hand across panes). Advisory to the caller;
   *  it is derived from the panes, never from geometry. */
  crossPane: boolean;
}

// ---- the recorded trace the classifier reads (geometry, discarded after) ----

/** One sampled pointer position. Coordinates are in the field SVG's own user
 *  space (viewBox units), resolved by the DOM layer before the trace reaches the
 *  classifier, so the classifier is pure geometry with no DOM. */
export interface TracePoint {
  x: number;
  y: number;
}

/** A recorded pointer trace: the ordered points from pointerdown to pointerup,
 *  how long the press lasted, and the objects under the start and the end. This
 *  is the classifier's INPUT; none of it is kept in the output past the touched
 *  set and the stroke kind (file 77 §5). */
export interface Trace {
  /** Ordered sampled points, pointerdown first, pointerup last. */
  points: TracePoint[];
  /** Press duration in milliseconds — distinguishes a HOLD from a tap. */
  durationMs: number;
  /** Objects the stroke crossed, in the order it first crossed them. Resolved by
   *  the DOM layer via hit-testing; the classifier does not touch the DOM. */
  touched: TouchedObject[];
  /** What a short back-and-forth crossed, when the DOM layer saw the trace cross
   *  a drawn line (a tie or a reading-member line) — the scratch's target
   *  (file 77 §2). Null when the trace crossed no such line. */
  crossedLine?: { line: "tie" | "reading_member"; madeByMe: boolean } | null;
}

// ---- recognition thresholds (viewBox units / ms) ----------------------------
//
// These are the ONLY tunables. They are expressed in the field SVG's user space
// so they are resolution-independent (the DOM layer maps client pixels into
// viewBox units before building the trace). Chosen so the six strokes do not
// collide: a hold barely moves; a tap is quick and still; a scratch is short and
// reverses; a connect/pull goes object-to-object; an encircle closes a loop.

/** A press this long (ms) with little movement is a HOLD, not a tap (file 77
 *  §1: Hold = press and hold). */
const HOLD_MS = 500;
/** Movement below this (viewBox units, total path length) is "did not move" —
 *  the difference between a hold/tap and a drawn stroke. */
const STILL_LEN = 10;
/** A stroke whose start and end are within this distance (viewBox units) is a
 *  CLOSED path — the candidate for an encircle (file 77 §1: a closed loop). */
const CLOSE_LEN = 44;
/** A closed path must also enclose at least this fraction of its bounding box to
 *  be a loop and not a thin there-and-back; keeps a scratch from reading as a
 *  degenerate loop. */
const LOOP_MIN_AREA_RATIO = 0.12;
/** A scratch is a SHORT stroke (its path length under this, viewBox units) that
 *  REVERSES direction across a line (file 77 §1: a short back-and-forth). */
const SCRATCH_MAX_LEN = 120;
/** A scratch must reverse its direction at least this many times. A single
 *  out-and-back (the minimal "back-and-forth" of file 77 §1) reverses once; a
 *  straight connect/pull has zero. The scratch is further separated from a
 *  connect by crossing a drawn line and touching no objects, so one reversal is
 *  enough signal without catching a plain drag. */
const SCRATCH_MIN_REVERSALS = 1;

// ---- pure geometry helpers (no DOM, no retained state) ----------------------

function dist(a: TracePoint, b: TracePoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Total path length of the trace (sum of segment lengths). */
function pathLength(pts: TracePoint[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += dist(pts[i - 1], pts[i]);
  return total;
}

/** The trace's bounding box area in viewBox units squared. */
function boundingArea(pts: TracePoint[]): number {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (!Number.isFinite(minX)) return 0;
  return (maxX - minX) * (maxY - minY);
}

/** The shoelace polygon area of the trace treated as a closed ring (absolute
 *  value). Used to tell a real loop (encloses area) from a thin there-and-back
 *  (near-zero area) once the path is known to close. */
function polygonArea(pts: TracePoint[]): number {
  if (pts.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/** How many times the trace reverses its dominant axis of travel — a scratch's
 *  signature (a back-and-forth has several; a straight drag has none). It
 *  projects each step onto the overall start→end direction and counts sign
 *  flips, so it is robust to a slightly curved connect. */
function reversals(pts: TracePoint[]): number {
  if (pts.length < 3) return 0;
  const start = pts[0];
  const end = pts[pts.length - 1];
  let axisX = end.x - start.x;
  let axisY = end.y - start.y;
  const netLen = Math.hypot(axisX, axisY);
  const totalLen = pathLength(pts);
  // A back-and-forth RETURNS near its start, so its net displacement is small
  // relative to the path it travelled. When the net vector is a poor axis (tiny,
  // or short against the total path), use the single LONGEST step as the axis of
  // travel instead — that is the direction the back-and-forth actually oscillates
  // along, so the sign flips are counted against a stable axis rather than noise.
  if (netLen < 1e-6 || netLen < totalLen * 0.4) {
    let bestLen = 0;
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i].x - pts[i - 1].x;
      const dy = pts[i].y - pts[i - 1].y;
      const l = Math.hypot(dx, dy);
      if (l > bestLen) {
        bestLen = l;
        axisX = dx;
        axisY = dy;
      }
    }
  }
  const nAxis = Math.hypot(axisX, axisY) || 1;
  const ux = axisX / nAxis;
  const uy = axisY / nAxis;
  let prevSign = 0;
  let count = 0;
  for (let i = 1; i < pts.length; i++) {
    const proj = (pts[i].x - pts[i - 1].x) * ux + (pts[i].y - pts[i - 1].y) * uy;
    const sign = proj > 0.5 ? 1 : proj < -0.5 ? -1 : 0;
    if (sign !== 0) {
      if (prevSign !== 0 && sign !== prevSign) count++;
      prevSign = sign;
    }
  }
  return count;
}

/** True when the trace's start and end are close enough to call the path closed. */
function isClosed(pts: TracePoint[]): boolean {
  if (pts.length < 3) return false;
  return dist(pts[0], pts[pts.length - 1]) <= CLOSE_LEN;
}

/** The distinct panes the touched objects sit in. */
function panesOf(objects: TouchedObject[]): Set<Pane> {
  const s = new Set<Pane>();
  for (const o of objects) s.add(o.pane);
  return s;
}

/** True when the touched set spans both split panes (file 75 §3). */
function spansSplit(objects: TouchedObject[]): boolean {
  const p = panesOf(objects);
  return p.has("left") && p.has("right");
}

// ---- the classifier: a PURE function, trace -> recognised stroke or null ----

/**
 * Classify a recorded pointer trace into one of the six strokes, or null when
 * the movement matches none (file 77 §1: "if a movement does not match one of
 * the six, the surface does nothing and leaves no mark"). It is PURE: the same
 * trace always yields the same result, it touches no DOM, and its output carries
 * the touched SET and the stroke kind only — never a coordinate (file 77 §5).
 *
 * The decision order matters, because the strokes are told apart by shape:
 *   1. HOLD   — a long press that barely moved, on exactly one object.
 *   2. SCRATCH— a short back-and-forth that crossed a drawn line (tie / member).
 *   3. ENCIRCLE — a closed loop that encloses real area, over two+ objects.
 *   4. PULL   — an open drag between two CHALLENGES (challenge → challenge).
 *   5. CONNECT— an open drag from one object to another (the general line).
 *   6. CARRY   is the press-and-move-an-object-onto-another drag; at the trace
 *      level it is indistinguishable from CONNECT (same open object→object
 *      path). The DOM layer decides carry vs connect by whether the pointer
 *      grabbed and moved an object (a drag of the object itself) rather than
 *      drawing a line between two at rest — it passes that intent in; the
 *      classifier treats an object→object open drag as CONNECT unless told the
 *      origin object was carried. So the classifier never INVENTS carry; the DOM
 *      layer, which alone knows "did the object move under the finger", marks it.
 *
 * A movement that is a tap (quick, still) is null here — tap stays select /
 * expand, handled by the existing HTMX affordances, not a stroke (file 77 §1).
 */
export function classify(trace: Trace, opts: { carried?: boolean } = {}): Recognised | null {
  const { points, durationMs, touched } = trace;
  if (points.length === 0) return null;

  const len = pathLength(points);
  const moved = len >= STILL_LEN;

  // 1. HOLD — a long press that barely moved, on exactly one object. A quick
  //    still press is a tap (select/expand), not a stroke, so it is null.
  if (!moved) {
    if (durationMs >= HOLD_MS && touched.length === 1) {
      return { stroke: "hold", objects: touched.slice(0, 1), crossPane: false };
    }
    return null;
  }

  // 2. SCRATCH — a SHORT back-and-forth across a drawn line (file 77 §1/§2). The
  //    DOM layer reports the crossed line; without one, a short zigzag means
  //    nothing (the ambiguity rule — never a fall-through to cut on a stray
  //    movement). The touched set is empty for a scratch: it addresses the LINE,
  //    which the server resolves from the scratch's crossed-line payload.
  if (
    trace.crossedLine &&
    len <= SCRATCH_MAX_LEN &&
    reversals(points) >= SCRATCH_MIN_REVERSALS
  ) {
    return { stroke: "scratch", objects: [], crossPane: false };
  }

  // 3. ENCIRCLE — a CLOSED loop that encloses real area, over two or more
  //    objects (file 77 §1: a closed loop around two or more objects). The area
  //    test rejects a thin there-and-back that happens to return near its start.
  if (isClosed(points) && touched.length >= 2) {
    const area = polygonArea(points);
    const bbox = boundingArea(points);
    if (bbox > 0 && area / bbox >= LOOP_MIN_AREA_RATIO) {
      return { stroke: "encircle", objects: touched, crossPane: spansSplit(touched) };
    }
  }

  // An open drag from here on needs two distinct endpoints (an origin object and
  // a destination object). Fewer than two touched objects on an open drag is a
  // line drawn to nowhere: nothing (file 77 §1).
  if (touched.length < 2) return null;

  const origin = touched[0];
  const dest = touched[touched.length - 1];

  // 4. PULL — an open drag between two CHALLENGES in different panes (file 77 §1:
  //    a long stroke from a challenge toward another challenge's pane). The
  //    server's grammar requires the two challenges to be in different panes
  //    (internal/stroke.pull), so cross-pane is the pull's shape.
  if (origin.kind === "challenge" && dest.kind === "challenge" && origin.pane !== dest.pane) {
    return { stroke: "pull", objects: [origin, dest], crossPane: true };
  }

  // 5/6. CARRY vs CONNECT — an open object→object drag. The DOM layer tells us
  //      whether the ORIGIN object was carried (grabbed and moved onto the
  //      destination, file 77 §1 Carry) rather than a line drawn between two at
  //      rest (Connect). The classifier never guesses carry from geometry; it is
  //      the same path either way, so the caller's `carried` intent decides.
  const objects = [origin, dest];
  const crossPane = origin.pane !== dest.pane && spansSplit(objects);
  if (opts.carried) {
    return { stroke: "carry", objects, crossPane };
  }
  return { stroke: "connect", objects, crossPane };
}

// ---- the form payload the server expects (internal/field PostStroke) --------

/**
 * Build the application/x-www-form-urlencoded body the server's PostStroke
 * endpoint reads (internal/field/write.go): `stroke=<kind>` and one `take=<id>`
 * per touched take, in touch order. Only TAKE ids are sent as `take` fields —
 * the server's wired act (the CONNECT tie) addresses two takes; other object
 * kinds in the touched set ride as `object` fields so a later server lane can
 * read them without a payload change, but today's server ignores them (it reads
 * `take`). A scratch sends its crossed line as `line`/`line_mine` so the server
 * can resolve cut vs weaken (internal/stroke.scratch). NO geometry is ever in
 * the body (file 77 §5).
 */
export function strokeFormBody(r: Recognised, crossedLine?: Trace["crossedLine"]): URLSearchParams {
  const body = new URLSearchParams();
  body.set("stroke", r.stroke);
  for (const o of r.objects) {
    if (o.kind === "take") body.append("take", o.id);
    else body.append("object", `${o.kind}:${o.id}`);
  }
  if (r.stroke === "scratch" && crossedLine) {
    body.set("line", crossedLine.line);
    body.set("line_mine", crossedLine.madeByMe ? "true" : "false");
  }
  return body;
}

/**
 * The challenge id a recognised stroke posts to. The server route is keyed to
 * ONE challenge (POST /challenge/{id}/stroke), and the wired act is two takes in
 * that one challenge. So the post target is the challenge both takes sit in. For
 * a SAME-PANE connect that is the pane's own challenge. For a CROSS-PANE stroke
 * there is NO single challenge (it is a cross tie, posted to /tie/cross via
 * crossTieFormBody, not this route), so this returns null and the caller takes
 * the cross-tie path instead.
 */
export function targetChallengeId(r: Recognised, paneChallenge: Record<Pane, string | null>): string | null {
  if (r.objects.length === 0) return null;
  if (r.crossPane) return null;
  const pane = r.objects[0].pane;
  return paneChallenge[pane] ?? null;
}

/**
 * The form body a CROSS-PANE stroke posts to the cross-tie route (canon 75 §3 /
 * file 77 §2 I-45): POST /tie/cross reads two takes and their two home
 * challenges, index-aligned — `take=<a>&take=<b>&challenge=<chA>&challenge=<chB>`
 * (internal/field.CrossTie). The two endpoints are the drag's origin and
 * destination (its first and last touched object); each object's pane resolves
 * to that pane's challenge via `paneChallenge`.
 *
 * Returns null when the stroke is NOT a cross tie the server can wire — only a
 * connect or carry whose two ends are BOTH takes, each in a different pane that
 * resolves to a DIFFERENT challenge, is one. A pull between challenge rings, an
 * endpoint that is not a take, an unresolved pane, or two ends in the same
 * challenge all yield null, and the caller leaves the settled ink with no post
 * (the quiet of a 204, file 77 §2). NO geometry is ever in the body (file 77 §5).
 */
export function crossTieFormBody(
  r: Recognised,
  paneChallenge: Record<Pane, string | null>,
): URLSearchParams | null {
  if (!r.crossPane) return null;
  if (r.stroke !== "connect" && r.stroke !== "carry") return null;
  if (r.objects.length < 2) return null;
  const origin = r.objects[0];
  const dest = r.objects[r.objects.length - 1];
  if (origin.kind !== "take" || dest.kind !== "take") return null;

  const aChallenge = paneChallenge[origin.pane];
  const bChallenge = paneChallenge[dest.pane];
  if (!aChallenge || !bChallenge || aChallenge === bChallenge) return null;

  const body = new URLSearchParams();
  // take[i] aligns with challenge[i]: the server reads the two lists pairwise.
  body.append("take", origin.id);
  body.append("take", dest.id);
  body.append("challenge", aChallenge);
  body.append("challenge", bChallenge);
  return body;
}
